import {
  resolveServiceDurationMinutes,
  TRAVEL_BUFFER_MINUTES,
} from './service-duration.util';

export function resolveOrderStartMs(order: any): number | null {
  const raw =
    order?.confirmed_service_time ??
    order?.scheduled_arrival_time ??
    order?.companyArrivalAt ??
    order?.scheduledAt ??
    (order?.scheduled ? order?.date : null) ??
    order?.scheduled_time;
  if (raw == null || raw === '') {
    return null;
  }
  if (typeof raw?.toDate === 'function') {
    const d = raw.toDate();
    return isNaN(d.getTime()) ? null : d.getTime();
  }
  if (typeof raw?.seconds === 'number') {
    return raw.seconds * 1000;
  }
  if (typeof raw === 'number') {
    return raw > 1e12 ? raw : raw * 1000;
  }
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d.getTime();
}

export function isScheduledOrder(order: any): boolean {
  return resolveOrderStartMs(order) != null;
}

/** Order occupies provider calendar (has a booked start time). */
export function hasBookedServiceTime(order: any): boolean {
  return resolveOrderStartMs(order) != null;
}

export function isActiveAssignmentStatus(status: string | undefined): boolean {
  const s = String(status || '')
    .toLowerCase()
    .replace(/[\s_-]/g, '');
  return (
    s === 'accepted' ||
    s === 'active' ||
    s === 'started' ||
    s === 'ontheway' ||
    s === 'inprogress'
  );
}

export interface ProviderBusyWindow {
  startMs: number;
  endMs: number;
  durationMinutes: number;
  travelBufferMinutes: number;
}

/** Busy window = service start → start + duration + travel buffer. */
export function getOrderBusyWindow(
  order: any,
  companyService?: any,
  travelBufferMinutes: number = TRAVEL_BUFFER_MINUTES
): ProviderBusyWindow | null {
  const startMs = resolveOrderStartMs(order);
  if (startMs == null) {
    return null;
  }
  const durationMinutes = resolveServiceDurationMinutes(order, companyService);
  const endMs =
    startMs + (durationMinutes + travelBufferMinutes) * 60 * 1000;
  return {
    startMs,
    endMs,
    durationMinutes,
    travelBufferMinutes,
  };
}

export function busyWindowsOverlap(
  a: ProviderBusyWindow,
  b: ProviderBusyWindow
): boolean {
  return a.startMs < b.endMs && b.startMs < a.endMs;
}

/** @deprecated Prefer busy-window overlap via findProviderScheduleConflict. */
export function ordersScheduleConflict(
  orderA: any,
  orderB: any,
  _bufferMinutes?: number
): boolean {
  const winA = getOrderBusyWindow(orderA);
  const winB = getOrderBusyWindow(orderB);
  if (!winA || !winB) {
    return false;
  }
  if (orderA?.id && orderB?.id && orderA.id === orderB.id) {
    return false;
  }
  return busyWindowsOverlap(winA, winB);
}

export interface ProviderScheduleConflict {
  conflictingOrder: any;
  existingStart: Date;
  existingEnd: Date;
  message: string;
}

function formatConflictTime(ms: number): string {
  try {
    return new Date(ms).toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return new Date(ms).toISOString();
  }
}

/**
 * Find first overlapping active order for this provider at the target order's time.
 */
export function findProviderScheduleConflict(
  providerId: string,
  targetOrder: any,
  allOrders: any[],
  companyService?: any
): ProviderScheduleConflict | null {
  if (!providerId || !targetOrder) {
    return null;
  }
  const targetWindow = getOrderBusyWindow(targetOrder, companyService);
  if (!targetWindow) {
    return null;
  }

  const targetId = targetOrder?.id;
  for (const order of allOrders || []) {
    if (!order) {
      continue;
    }
    if (targetId && order.id === targetId) {
      continue;
    }
    if (order === targetOrder) {
      continue;
    }
    if (order.providerId !== providerId) {
      continue;
    }
    if (!isActiveAssignmentStatus(order.status)) {
      continue;
    }
    if (!hasBookedServiceTime(order)) {
      continue;
    }
    const existingWindow = getOrderBusyWindow(order);
    if (!existingWindow) {
      continue;
    }
    if (!busyWindowsOverlap(targetWindow, existingWindow)) {
      continue;
    }
    const existingStart = new Date(existingWindow.startMs);
    const existingEnd = new Date(existingWindow.endMs);
    return {
      conflictingOrder: order,
      existingStart,
      existingEnd,
      message: `Provider is busy. This provider has another order from ${formatConflictTime(
        existingWindow.startMs
      )} to ${formatConflictTime(existingWindow.endMs)}.`,
    };
  }
  return null;
}

export function providerHasScheduleConflict(
  providerId: string,
  targetOrder: any,
  allOrders: any[],
  companyService?: any
): boolean {
  return !!findProviderScheduleConflict(
    providerId,
    targetOrder,
    allOrders,
    companyService
  );
}

/** Build a synthetic order-like object for conflict checks at a proposed time. */
export function buildProposedAssignmentOrder(
  baseOrder: any,
  scheduledTime: Date,
  overrides?: Record<string, unknown>
): any {
  return {
    ...(baseOrder || {}),
    confirmed_service_time: scheduledTime,
    scheduled_arrival_time: scheduledTime,
    scheduledAt: scheduledTime,
    scheduled: true,
    ...(overrides || {}),
  };
}
